import React from 'react';
import { getTitleWithTooltip } from '../../../../agent-production/helpers/schemaTableHelpers';
import CustomHeader from './CustomHeader';
import { tableViewNames } from '../../../../agent-production/constants/agentProductionConstants';
import { dividerClassNames } from './colSettings';
import { cellRenderers } from './tableViews';
import { useSearchStore } from '../../../../store/search/store';
import { retainTerms as rt } from './retainedSettings';
import { CROSS_SORT_TABLES } from './crossSort';

const { getTableRetainedStateByType, setTableRetainedStateByType } = useSearchStore.getState();

function headerWrapper(text) {
    return (
        <p
            className='ag-header-group-text mx-auto ag-grid-cell-link text-uppercase text-wrap text-center m-2'
            role='presentation'
        >
            {text}
        </p>
    );
}

export function getAdditionalTableHeader(field, props) {
    const { additionalTableHeaders } = props;
    return headerWrapper(additionalTableHeaders[0][field]?.title);
}

export function getContact(props) {
    const { additionalTableHeaders } = props;
    return headerWrapper(additionalTableHeaders[0].contact.title);
}

export function getCurrentSearchLast6Months(props) {
    const { additionalTableHeaders } = props;
    return headerWrapper(additionalTableHeaders[0].search.title);
}

export function getAllMlsLast12Months(props) {
    const { additionalTableHeaders } = props;
    return headerWrapper(additionalTableHeaders[0].mls.title);
}

export function getGrowth(props) {
    const { additionalTableHeaders } = props;
    return headerWrapper(additionalTableHeaders[0].growth.title);
}

const setDefaultSort = (e, tableView) => {
    const { defaultSort } = tableView;
    if (defaultSort) {
        const state = useSearchStore.getState();
        const selectedModule = state.selectedModule;
        const crossSortSourceTableId = state[selectedModule]?.crossSortSourceTableId;

        if (crossSortSourceTableId && CROSS_SORT_TABLES.includes(tableView.id)) {
            return;
        }
        const columnState = {
            state: [
                {
                    colId: defaultSort.colId,
                    sort: defaultSort.sortDir
                }
            ]
        };
        e.api.applyColumnState(columnState);
    }
};

const addRemoveHighlights = (elements, columnIndex, highlightClass) => {
    elements?.forEach(element => {
        const ATTR_COLUMN_INDEX = 'aria-colindex';
        if (element.getAttribute(ATTR_COLUMN_INDEX) === columnIndex.toString()) {
                element.classList.add(highlightClass);
            } else {
                element.classList.remove(highlightClass);
            }
    });
};

const drawHeaderGroupDivider = () => {
    const headerGroupCellElements = document.querySelectorAll(`.ag-header-group-cell`)
    headerGroupCellElements.forEach((element, index) => {
        const headerResizeElement = element.querySelectorAll('.ag-header-cell-resize')?.[0];
        headerResizeElement.classList.add(dividerClassNames.transparentResizeIcon);
        if (index !== headerGroupCellElements.length - 1){
            element.classList.add(dividerClassNames.dividerCol);
        }
    });
};

export const handleDrawDivider = (tableName, columnDefs) => {
    if (tableName !== tableViewNames.overview) return;
    drawHeaderGroupDivider();

    let colIndex = 0;
    let lastShownColumnDefWithIndex = [];

    const hiddenDividers = document.querySelectorAll(`.ag-header-cell > div.ag-header-cell-resize.${dividerClassNames.transparentResizeIcon}`);
    hiddenDividers?.forEach(divider => {
        divider.classList.remove(dividerClassNames.transparentResizeIcon)
    });

    const filterDividers = document.querySelectorAll(`.ag-floating-filter.${dividerClassNames.dividerCol}`);
    filterDividers?.forEach(divider => divider.classList.remove(dividerClassNames.dividerCol))

    lastShownColumnDefWithIndex?.forEach(({id}) => {
        const el = document.querySelector(`.ag-floating-filter[aria-colindex='${id}']`);
        el && el.classList.remove(dividerClassNames.dividerCol);
    });

    columnDefs.forEach((columDef, index) => {
        columDef.children.forEach((child, childIndex) => {
            colIndex++;
            const newArray = columDef.children.slice();
            const lastShownChild = newArray.reverse().find(child => !child.initialHide);
            const lastShownChildIndex = columDef.children.findIndex(child => {
                return child?.field === lastShownChild?.field;
            });
            const lastShownChildren = columDef.children[lastShownChildIndex];
            const lastIndex = columnDefs.length - 1;
            if (index !== lastIndex) {
                lastShownChildren.cellClass = lastShownChildren.cellClass + ` ${dividerClassNames.dividerCol}`;
            }
            if (childIndex === lastShownChildIndex && lastShownChildren) {
                lastShownColumnDefWithIndex.push({...child, id: colIndex, group: index});
            }
        })
    });
    lastShownColumnDefWithIndex.forEach(({id}) => {
        const floatingFilterElement = document.querySelector(`.ag-floating-filter[aria-colindex='${id}']`);
        floatingFilterElement && floatingFilterElement.classList.add(dividerClassNames.dividerCol);
        const headerElement = document.querySelector(`.ag-header-cell[aria-colindex='${id}']`);
        headerElement && headerElement.classList.add(dividerClassNames.dividerCol);
    });

    const dividers = document.querySelectorAll(`.ag-header-cell.${dividerClassNames.dividerCol} > div.ag-header-cell-resize`)
    dividers.forEach(divider => {
        divider.classList.add(dividerClassNames.transparentResizeIcon);
    });

};

export const handleSortedHighlight = (e, tableView, crossSortSourceTableId) => {

    const columns = e.api.getColumns();
    if (!columns?.length || !tableView) return;

    const CLASS_HIGHLIGHT = 'highlighted-column';
    const filteredColumnElements = document.querySelectorAll(`#${tableView?.id} .ag-floating-filter.ag-header-cell`);
    const headerCellElements = document.querySelectorAll(`#${tableView?.id} .ag-header-cell`);

    const clearHighlights = (sortedColumn = null) => {
        const sortedColumnIndex = sortedColumn ? e.api.getColumns()?.indexOf(sortedColumn) + 1 : -1;
        addRemoveHighlights(headerCellElements, sortedColumnIndex, CLASS_HIGHLIGHT);
        addRemoveHighlights(filteredColumnElements, sortedColumnIndex, CLASS_HIGHLIGHT);

        columns?.forEach((column) => {
            if (column === sortedColumn) {
                column.getColDef().cellClass = `${sortedColumn.userProvidedColDef.cellClass} ${CLASS_HIGHLIGHT}`;
            }
            if (column !== sortedColumn) {
                column.getColDef().cellClass = column.userProvidedColDef.cellClass;
            }
        });

        setTimeout(() => e.api.refreshCells({ force: true, suppressFlash: true }), 10);
    };

    const isSourceTable = tableView?.id === crossSortSourceTableId;

    if (crossSortSourceTableId && !isSourceTable && CROSS_SORT_TABLES.includes(tableView?.id)) {
        clearHighlights();
        return;
    }

    const sortedColumn = columns?.find(column => column.isSorting() && (column.getSortIndex() ?? 0) === 0);

    if (!sortedColumn) {
        const savedSortState = getTableRetainedStateByType({ tableId: tableView?.id, type: rt.sort });
        if (!savedSortState) setDefaultSort(e, tableView);
        return;
    }

    const sortState = { colId: sortedColumn.getColId(), sort: sortedColumn.getSort() };
    setTableRetainedStateByType({ tableId: tableView?.id, type: rt.sort, sortState });
    clearHighlights(sortedColumn);
};

const generateChildDefs = (item, columns, columnDefs, additionalTableHeaders, tableTitleWithTooltips, externalParams) => {
    const headerGroup = {
        headerGroupComponent: item.headerGroupTitle,
        headerGroupComponentParams: { additionalTableHeaders }
    };
    const childrenObjects = [];
    item.children.map(child => {
        const titleWithTooltip = getTitleWithTooltip(tableTitleWithTooltips[child.col.id], '', false, externalParams?.tooltipTargetPrefix);
        const filterProps = child.filterOptions && {
            ...child.filterOptions,
            floatingFilter: true,
            suppressHeaderMenuButton: true
        };
        const extControl = columns?.filter(col => col.id === child.col.id)[0];
        childrenObjects.push({
            field: child.col.id,
            headerName: child.col.label,
            initialHide: !!extControl ? !extControl?.isDisplayed : !!child.initialHide,
            cellClassRules: child.cellClassRules,
            cellRendererSelector: (params) => {
                if(!params.node.rowPinned || !externalParams?.summaryPinnedConfig) return {
                    component: child.cellRenderer
                };
                const { cellsToShow, cellsToOverride } = externalParams?.summaryPinnedConfig || {};
                const colId = child.col.id;
                const isCellToShow = cellsToShow.includes(colId);
                const cellOverride = cellsToOverride[colId];
                if (isCellToShow) return {component: child.cellRenderer};
                if (!isCellToShow && !cellOverride) return {component: () => cellRenderers.pinnedRow()};
                if (cellOverride) return { component: () => cellRenderers.pinnedRow(cellOverride) };
            },
            cellRendererParams: externalParams || null,
            headerComponentParams: { titleWithTooltip },
            ...filterProps,
            ...child.colDef
        });
    });
    columnDefs.push({ ...headerGroup, children: childrenObjects });
};

export const generateTableColDefs = ({
                                         columns,
                                         tableView,
                                         additionalTableHeaders,
                                         tableTitleWithTooltips,
                                         externalParams
                                     }) => {
    let columnDefs = [];
    let columnDefsLevel2 = [];
    if (!tableView) return columnDefs;

    tableView?.tables?.map(item => {
        // superHeader level2 columnDefs building logic.
        const hasSecondLevelHeaders = item.children?.length > 0 && item.children[0]?.children;
        if (hasSecondLevelHeaders) {
            const headerGroupLevel2 = {
                headerGroupComponent: item.headerGroupTitle,
                headerGroupComponentParams: { additionalTableHeaders }
            };
            item.children.map(group => {
                generateChildDefs(group, columns, columnDefsLevel2, additionalTableHeaders, tableTitleWithTooltips, externalParams);
            });
            columnDefs.push({ ...headerGroupLevel2, children: columnDefsLevel2 });
            columnDefsLevel2 = [];
        }

        // superHeader level1 columnDefs building logic.
        const hasFirstLevelHeaders = item.children && Array.isArray(item.children);
        if (hasFirstLevelHeaders && !hasSecondLevelHeaders ) {
            generateChildDefs(item, columns, columnDefs, additionalTableHeaders, tableTitleWithTooltips, externalParams);
        }

        // No-superHeaders columnDefs building logic.
        if (!hasSecondLevelHeaders && !hasFirstLevelHeaders ) {
            const titleWithTooltip = <CustomHeader headerDetails={tableTitleWithTooltips[item.col?.id]} />;
            const filterOptions = item?.filterOptions ? item.filterOptions : {};
	        const isDefaultSort = tableView?.defaultSort?.colId === item.col.id;
            const isBreakdown = tableView?.defaultSort?.isBreakdown;
            const savedSortState = getTableRetainedStateByType({tableId: tableView?.id, type: rt.sort});
            const shouldApplyDefaultSort = !savedSortState && !isBreakdown && isDefaultSort;

            const colDef = {
                field: item.col.keyField || item.col.id,
                ...(shouldApplyDefaultSort && { sort: tableView?.defaultSort?.sortDir }),
                headerName: item.col.label,
                initialHide: !!item.initialHide,
                cellClassRules: item.cellClassRules,
                cellRenderer: item.cellRenderer,
                cellRendererParams: externalParams || null,
                headerComponentParams: { titleWithTooltip },
                ...filterOptions,
                ...item.colDef
            };
            columnDefs.push(colDef);
        }
    });
    return columnDefs;
};

export const AG_GRID_LICENSE = `Using_this_{AG_Grid}_Enterprise_key_{AG-082526}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Lone_Wolf_Technologies_and_affiliates}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{BrokerMetrics}_only_for_{4}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{BrokerMetrics}_need_to_be_licensed___{BrokerMetrics}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{4_August_2026}____[v3]_[01]_MTc4NTc5ODAwMDAwMA==4f732ca6e2f29be9fcd9fc9b5e392c29`;
